import { NO, YES } from "uicore-ts"
import { RHColor } from "../RHColor"
import { RHTextSanitizer } from "./RHTextSanitizer"


export type RHProcurementPartDisplayPart = {
    partNumber?: number
    description?: string
}


export class RHProcurementPartDisplay {
    static readonly evaluationMovedToNextRoundText = "Hindamine viidi järgmisesse vooru."
    
    static shouldShowPartIdentityForPartCount(partCount: number | undefined): boolean {
        if (partCount === 1) {
            return NO
        }
        return YES
    }
    
    static shouldShowPartIdentityForParts(parts: object[] | undefined): boolean {
        if (!parts) {
            return YES
        }
        return RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(parts.length)
    }
    
    static partHeaderText(part: RHProcurementPartDisplayPart, partCount: number | undefined): string {
        if (!RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            return ""
        }
        if (part.partNumber === undefined) {
            return ""
        }
        return `Osa ${part.partNumber}`
    }
    
    static partPreviewTitle(part: RHProcurementPartDisplayPart, partCount: number | undefined): string {
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            const partHeader = RHProcurementPartDisplay.partHeaderText(part, partCount)
            if (partHeader) {
                return partHeader
            }
            return "Pakkumus"
        }
        const description = RHTextSanitizer.plainText(part.description ?? "").trim()
        if (description) {
            return description
        }
        return "Pakkumus"
    }
    
    static partPreviewTitleWithDescription(
        part: RHProcurementPartDisplayPart,
        partCount: number | undefined
    ): string {
        if (!RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            return RHProcurementPartDisplay.partPreviewTitle(part, partCount)
        }
        
        const partTitle = RHProcurementPartDisplay.partHeaderText(part, partCount)
        const description = RHTextSanitizer.plainText(part.description ?? "").trim()
        if (!partTitle) {
            return RHProcurementPartDisplay.partPreviewTitle(part, partCount)
        }
        if (!description) {
            return partTitle
        }
        return `${partTitle} – ${description}`
    }
    
    static descriptionLabelHTML(partCount: number | undefined, isRequired: boolean = YES): string {
        let labelText = "Kirjeldus"
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            labelText = "Osa kirjeldus"
        }
        if (isRequired) {
            return `${labelText} <span style="color: ${RHColor.danger.stringValue}">*</span>`
        }
        return labelText
    }
    
    static descriptionPlaceholderText(partCount: number | undefined): string {
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            return "Kirjelda seda osa"
        }
        return "Kirjelda hanget"
    }
    
    static estimatedCostPlaceholderText(partCount: number | undefined): string {
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            return "Osa eeldatav maksumus (KM-ta)"
        }
        return "Hanke eeldatav maksumus (KM-ta)"
    }
    
    static detailsPartsHeaderLabelText(partCount: number | undefined): string {
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            return "Hankeosad:"
        }
        return "Kirjeldus:"
    }
    
    static requiredDocumentsExplanationText(partCount: number | undefined): string {
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            return "Lisa dokumendid, mida pakkuja peab pakkumuse esitamisel üles laadima. Saad täpsustada, millistele osadele iga dokument kehtib."
        }
        return "Lisa dokumendid, mida pakkuja peab pakkumuse esitamisel üles laadima."
    }
    
    static requiredDocumentValidationPrefix(part: RHProcurementPartDisplayPart, partCount: number | undefined): string {
        const partHeader = RHProcurementPartDisplay.partHeaderText(part, partCount)
        if (!partHeader) {
            return ""
        }
        return `${partHeader}: `
    }
    
    static noPartOffersText(partCount: number | undefined): string {
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            return "Sellele osale ei esitatud pakkumusi"
        }
        return "Sellele hankele ei esitatud pakkumusi"
    }

    static finalizedEvaluationText(partCount: number | undefined): string {
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            return "✓ Selle osa hindamine on lõpetatud. Pakkujaid on teavitatud."
        }
        return "✓ Hindamine on lõpetatud. Pakkujaid on teavitatud."
    }

    static closedEvaluationWithoutWinnerText(partCount: number | undefined): string {
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            return "Selle osa hindamine on lõpetatud. Ühtegi pakkumust ei tunnistatud edukaks ja lepingut ei sõlmita."
        }
        return "Hindamine on lõpetatud. Ühtegi pakkumust ei tunnistatud edukaks ja lepinguid ei loodud."
    }

    static closeWithoutContractActionButtonText(part: RHProcurementPartDisplayPart, partCount: number | undefined): string {
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            return `Lõpeta Osa ${part.partNumber} ilma lepinguta`
        }
        return "Lõpeta ilma lepinguta"
    }

    static closedWithoutContractStatusText(partCount: number | undefined): string {
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            return "⚠ Osa on lõpetatud ilma lepinguta — sellele ei saa enam pakkumusi esitada."
        }
        return "⚠ Hange on lõpetatud ilma lepinguta — pakkumusi ei saa enam esitada."
    }

}
